import React, { useEffect, useState } from 'react';
import { nb } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { X, SlidersHorizontal, RotateCcw, CalendarDays, CalendarRange, ChevronDown, GitCompare, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { useFilters } from '@/state/filters';
import { DEMO_WINDOW } from '@/fixtures/scenarios';
import { addDays, formatDateShort, formatRangeShort, isoToLocalDate, localDateToIso, parseNbDate } from '@/domain/time';
import { cn } from '@/lib/utils';

const CONTAINER = 'mx-auto w-full max-w-[1720px] px-4 sm:px-6 lg:px-8';

/* Felles utseende for kompakte kontroller i filterraden */
const CONTROL = 'h-9 rounded-[10px] border border-[var(--hr-border)] bg-white text-[14px] font-medium text-[var(--hr-text)] shadow-none transition-colors duration-150 hover:border-[rgba(31,33,48,0.28)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] focus-visible:ring-offset-0';
const SELECT_TRIGGER = cn(CONTROL, 'w-auto gap-2 px-3 focus:ring-2 focus:ring-[var(--hr-focus)] focus:ring-offset-0 data-[state=open]:border-[var(--hr-focus)] [&>span]:flex [&>span]:items-center [&>span]:gap-1.5');

function Field({ label, htmlFor, children, className }) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor} className="text-[13px] font-medium text-[var(--hr-muted)]">
        {label}
      </Label>
      {children}
    </div>
  );
}

/**
 * Datofelt med entydig norsk visning (dd.mm.åååå) uavhengig av nettleserspråk:
 * tekstfelt som tolker dd.mm.åååå / ISO, pluss kalender (nb-NO) i popover.
 * `bare` gir et rammeløst felt for bruk inne i en gruppert kontroll.
 */
export function DateField({ id, value, min = DEMO_WINDOW.from, max = DEMO_WINDOW.to, onChange, testId, label, bare = false }) {
  const [text, setText] = useState(formatDateShort(value));
  const [open, setOpen] = useState(false);
  useEffect(() => setText(formatDateShort(value)), [value]);
  const commit = () => {
    const iso = parseNbDate(text);
    if (iso && iso >= min && iso <= max) {
      if (iso !== value) onChange(iso);
      setText(formatDateShort(iso));
    } else {
      if (text.trim() && (!iso || iso < min || iso > max)) toast.message('Ugyldig dato', { description: `Skriv dd.mm.åååå innenfor ${formatDateShort(min)} – ${formatDateShort(max)}.` });
      setText(formatDateShort(value));
    }
  };
  return (
    <div className={cn('flex items-stretch overflow-hidden', bare ? 'h-full' : cn(CONTROL, 'min-w-0 flex-1 focus-within:border-[var(--hr-focus)] focus-within:ring-2 focus-within:ring-[var(--hr-focus)]'))}>
      <input
        id={id}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') setText(formatDateShort(value));
        }}
        inputMode="numeric"
        placeholder="dd.mm.åååå"
        aria-label={label}
        className={cn('hr-tabular min-w-0 bg-transparent text-[14px] font-medium text-[var(--hr-text)] outline-none placeholder:text-[var(--hr-muted-2)]', bare ? 'w-[96px] px-2 text-center focus-visible:bg-[var(--hr-surface-2)]' : 'flex-1 px-3')}
        data-testid={testId}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" aria-label={`Åpne kalender for ${label}`} className={cn('flex shrink-0 items-center justify-center text-[var(--hr-muted)] transition-colors duration-150 hover:bg-[var(--hr-surface-2)] hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:bg-[var(--hr-accent-soft)]', bare ? 'w-8' : 'w-9 border-l border-[var(--hr-hairline)]')} data-testid={`${testId}-calendar`}>
            <CalendarDays className="h-4 w-4" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto rounded-[12px] p-0 shadow-[var(--hr-shadow-md)]" data-testid={`${testId}-calendar-popover`}>
          <Calendar
            mode="single"
            locale={nb}
            weekStartsOn={1}
            selected={isoToLocalDate(value)}
            defaultMonth={isoToLocalDate(value)}
            fromDate={isoToLocalDate(min)}
            toDate={isoToLocalDate(max)}
            onSelect={(d) => {
              if (d) {
                onChange(localDateToIso(d));
                setOpen(false);
              }
            }}
            initialFocus
          />
          <div className="border-t border-[var(--hr-hairline)] px-3 py-2 text-[12px] text-[var(--hr-muted)]">Datavindu {formatDateShort(min)} – {formatDateShort(max)} · Europe/Oslo</div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function usePresets() {
  const to = DEMO_WINDOW.to;
  return [
    { id: 'okt-1', label: '1. oktober', from: '2026-10-01', to: '2026-10-01' },
    { id: 'okt-2', label: '2. oktober', from: '2026-10-02', to: '2026-10-02' },
    { id: 'okt-3', label: '3. oktober', from: '2026-10-03', to: '2026-10-03' },
    { id: 'okt-1-2', label: '1.–2. oktober', from: '2026-10-01', to: '2026-10-02' },
    { id: 'siste-7', label: 'Siste 7 dager', from: addDays(to, -6), to },
    { id: 'siste-14', label: 'Siste 14 dager', from: addDays(to, -13), to },
    { id: 'siste-30', label: 'Siste 30 dager', from: addDays(to, -29), to },
    { id: 'september', label: 'September', from: '2026-09-01', to: '2026-09-30' },
  ];
}

function PeriodPresets({ f, className }) {
  const presets = usePresets();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={cn('inline-flex h-full items-center gap-1 px-2.5 text-[14px] font-medium text-[var(--hr-muted)] transition-colors duration-150 hover:bg-[var(--hr-surface-2)] hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:bg-[var(--hr-accent-soft)] data-[state=open]:bg-[var(--hr-surface-2)] data-[state=open]:text-[var(--hr-heading)]', className)} data-testid="period-presets-button" aria-label="Hurtigvalg for periode">
          Hurtigvalg <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={6} className="w-52 rounded-[12px] p-1.5 shadow-[var(--hr-shadow-md)]">
        <DropdownMenuLabel className="px-2 pb-1 pt-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">Periode</DropdownMenuLabel>
        {presets.slice(0, 4).map((p) => (
          <DropdownMenuItem key={p.id} className="rounded-[8px]" onSelect={() => f.setPeriod(p.from, p.to)} data-testid={`period-preset-${p.id}`}>
            {p.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        {presets.slice(4).map((p) => (
          <DropdownMenuItem key={p.id} className="rounded-[8px]" onSelect={() => f.setPeriod(p.from, p.to)} data-testid={`period-preset-${p.id}`}>
            {p.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Én kompakt periodekontroll: viser valgt periode, åpner datoer og hurtigvalg i popover. */
function PeriodControl({ f }) {
  const presets = usePresets();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={cn(CONTROL, 'inline-flex items-center gap-2 px-3 data-[state=open]:border-[var(--hr-focus)]')} data-testid="filters-period-control" aria-label={`Periode ${formatRangeShort(f.fromDate, f.toDate)}`}>
          <CalendarDays className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
          <span className="hr-tabular">{formatRangeShort(f.fromDate, f.toDate)}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[360px] rounded-[12px] p-4 shadow-[var(--hr-shadow-md)]" data-testid="filters-period-popover">
        <Field label="Periode" htmlFor="filters-from">
          <div className="flex items-center gap-2">
            <DateField id="filters-from" label="Fra dato" value={f.fromDate} max={f.toDate} onChange={(iso) => f.set({ fra: iso, til: iso > f.toDate ? iso : f.toDate })} testId="filters-from-date" />
            <span className="text-sm text-[var(--hr-muted-2)]" aria-hidden="true">–</span>
            <DateField id="filters-to" label="Til dato" value={f.toDate} min={f.fromDate} onChange={(iso) => f.set({ fra: f.fromDate, til: iso < f.fromDate ? f.fromDate : iso })} testId="filters-to-date" />
          </div>
        </Field>
        <div className="mt-4 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">Hurtigvalg</div>
        <div className="mt-2 grid grid-cols-2 gap-1" data-testid="period-presets">
          {presets.map((p) => {
            const active = f.fromDate === p.from && f.toDate === p.to;
            return (
              <button key={p.id} type="button" onClick={() => f.setPeriod(p.from, p.to)} className={cn('h-8 rounded-[8px] px-2.5 text-left text-[14px] transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', active ? 'bg-[var(--hr-accent-soft)] font-semibold text-[var(--hr-heading)]' : 'text-[var(--hr-text)]')} aria-pressed={active} data-testid={`period-preset-${p.id}`}>
                {p.label}
              </button>
            );
          })}
        </div>
        <div className="mt-3 border-t border-[var(--hr-hairline)] pt-2.5 text-[12px] text-[var(--hr-muted-2)]">Datavindu {formatDateShort(DEMO_WINDOW.from)} – {formatDateShort(DEMO_WINDOW.to)} · Europe/Oslo · Gjelder alle sider og ligger i adressen.</div>
      </PopoverContent>
    </Popover>
  );
}

/** Referanseperiode ved manuell sammenligning: kompakt knapp med datoer i popover. */
function ReferenceControl({ f }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={cn(CONTROL, 'inline-flex items-center gap-2 px-3 data-[state=open]:border-[var(--hr-focus)]')} data-testid="filters-reference-control" aria-label={`Referanseperiode ${formatRangeShort(f.compare.fromDate, f.compare.toDate)}`}>
          <CalendarRange className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
          <span className="text-[var(--hr-muted)]">Ref.</span>
          <span className="hr-tabular">{formatRangeShort(f.compare.fromDate, f.compare.toDate)}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[360px] rounded-[12px] p-4 shadow-[var(--hr-shadow-md)]">
        <ManualRange f={f} />
      </PopoverContent>
    </Popover>
  );
}

function CompareSelect({ f, id = 'filters-compare', className, labelled = false }) {
  const compareValue = f.compareMode === 'manuell' ? 'manuell' : f.compareMode;
  return (
    <Select
      value={compareValue}
      onValueChange={(v) => {
        if (v === 'forrige') f.set({ sml: null });
        else if (v === 'scenario') f.set({ sml: 'scenario' });
        else f.set({ sml: `${f.compare.fromDate}_${f.compare.toDate}` });
      }}
    >
      <SelectTrigger id={id} className={cn(labelled ? 'h-10 rounded-[10px] bg-white' : SELECT_TRIGGER, className)} data-testid="filters-comparison-select" aria-label="Sammenligning">
        {!labelled && <GitCompare className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />}
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-[10px]">
        <SelectItem value="forrige">Forrige periode</SelectItem>
        <SelectItem value="scenario">Scenarioreferanse</SelectItem>
        <SelectItem value="manuell">Manuelt intervall</SelectItem>
      </SelectContent>
    </Select>
  );
}

function ScenarioSelect({ f, id = 'filters-scenario', className, labelled = false }) {
  const onScenario = (sid) => {
    if (sid === f.scenarioId) return;
    const sc = f.setScenario(sid);
    toast.message(`Scenario «${sc.name}»`, { description: `Perioden ble satt til ${formatRangeShort(sc.recommendedFrom, sc.recommendedTo)}. Andre filtre ble nullstilt.` });
  };
  return (
    <Select value={f.scenarioId} onValueChange={onScenario}>
      <SelectTrigger id={id} className={cn(labelled ? 'h-10 rounded-[10px] bg-white' : SELECT_TRIGGER, className)} data-testid="filters-scenario-select" aria-label="Scenario">
        {!labelled && <Layers className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />}
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-[10px]">
        {f.scenarios.map((s) => (
          <SelectItem key={s.id} value={s.id} data-testid={`filters-scenario-option-${s.id}`}>
            {s.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SiteSelect({ f, id = 'filters-site' }) {
  return (
    <Select value={f.siteId} onValueChange={(v) => f.set({ anlegg: v === 'dimmelsvik' ? null : v })}>
      <SelectTrigger id={id} className="h-10 rounded-[10px] bg-white" data-testid="filters-site-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-[10px]">
        {f.sites.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            {s.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ScopeSelect({ f, id = 'filters-scope' }) {
  return (
    <Select value={f.scope === 'all' ? 'alle' : 'produksjon'} onValueChange={(v) => f.set({ omfang: v === 'alle' ? 'alle' : null })}>
      <SelectTrigger id={id} className="h-10 rounded-[10px] bg-white" data-testid="filters-scope-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-[10px]">
        <SelectItem value="produksjon">Produksjonsområde (8 maskiner)</SelectItem>
        <SelectItem value="alle">Alle maskiner</SelectItem>
      </SelectContent>
    </Select>
  );
}

function ManualRange({ f, idPrefix = 'filters' }) {
  return (
    <div className="flex flex-col gap-1.5" data-testid="filters-manual-range">
      <span className="text-[13px] font-medium text-[var(--hr-muted)]">Referanseperiode</span>
      <div className="flex items-center gap-2">
      <DateField id={`${idPrefix}-cfrom`} label="Referanse fra" value={f.compare.fromDate} onChange={(iso) => f.set({ sml: `${iso}_${f.compare.toDate < iso ? iso : f.compare.toDate}` })} testId="filters-compare-from-date" />
      <span className="text-sm text-[var(--hr-muted-2)]" aria-hidden="true">–</span>
      <DateField id={`${idPrefix}-cto`} label="Referanse til" value={f.compare.toDate} min={f.compare.fromDate} onChange={(iso) => f.set({ sml: `${f.compare.fromDate}_${iso < f.compare.fromDate ? f.compare.fromDate : iso}` })} testId="filters-compare-to-date" />
      </div>
    </div>
  );
}

/** «Flere filtre»: sekundære valg som sjelden endres. */
function MoreFilters({ f }) {
  const count = (f.scope === 'all' ? 1 : 0) + (f.siteId !== 'dimmelsvik' ? 1 : 0);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={cn(CONTROL, 'inline-flex items-center gap-2 px-3 data-[state=open]:border-[var(--hr-focus)]')} data-testid="filters-more-button" aria-label="Flere filtre">
          <SlidersHorizontal className="h-4 w-4 text-[var(--hr-muted-2)]" aria-hidden="true" />
          Flere filtre
          {count > 0 && (
            <span className="hr-tabular inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[var(--hr-navy)] px-1.5 text-[12px] font-semibold text-white" data-testid="filters-more-count">
              {count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[300px] rounded-[12px] p-4 shadow-[var(--hr-shadow-md)]" data-testid="filters-more-popover">
        <div className="space-y-4">
          <Field label="Anlegg" htmlFor="filters-site">
            <SiteSelect f={f} />
          </Field>
          <Field label="Omfang" htmlFor="filters-scope">
            <ScopeSelect f={f} />
            <p className="text-[12px] leading-4 text-[var(--hr-muted-2)]">«Alle maskiner» tar med utlasting og service, og er ikke sammenlignbart med produksjonsområdet.</p>
          </Field>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ActiveChips({ f, className }) {
  if (!f.chips.length) return null;
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)} data-testid="active-filter-chips">
      {f.chips.map((c) => (
        <span key={c.key} data-testid={`active-filter-chip-${c.key}`} className="inline-flex h-7 items-center gap-0.5 rounded-full border border-[var(--hr-hairline)] bg-white py-0 pl-2.5 pr-0.5 text-[13px] font-medium text-[var(--hr-text)]">
          {c.label}
          <button type="button" onClick={() => f.set(c.clear)} aria-label={`Fjern filter: ${c.label}`} className="flex h-6 w-6 items-center justify-center rounded-full text-[var(--hr-muted)] transition-colors duration-150 hover:bg-[var(--hr-inset)] hover:text-[var(--hr-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={`active-filter-chip-${c.key}-remove`}>
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        </span>
      ))}
      <Button variant="ghost" size="sm" className="h-7 rounded-full px-2.5 text-[13px] text-[var(--hr-muted)] hover:text-[var(--hr-heading)]" onClick={f.reset} data-testid="filters-reset-button">
        <RotateCcw className="mr-1 h-3 w-3" aria-hidden="true" /> Nullstill
      </Button>
    </div>
  );
}

/** Mobil/nettbrett: alle felt stablet i skuff. */
function StackedFields({ f }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Scenario" htmlFor="filters-m-scenario">
        <ScenarioSelect f={f} id="filters-m-scenario" labelled />
      </Field>
      <Field label="Sammenligning" htmlFor="filters-m-compare">
        <CompareSelect f={f} id="filters-m-compare" labelled />
      </Field>
      <Field label="Periode" htmlFor="filters-m-from" className="sm:col-span-2">
        <div className="flex flex-wrap items-center gap-2">
          <DateField id="filters-m-from" label="Fra dato" value={f.fromDate} max={f.toDate} onChange={(iso) => f.set({ fra: iso, til: iso > f.toDate ? iso : f.toDate })} testId="filters-m-from-date" />
          <span className="text-sm text-[var(--hr-muted-2)]" aria-hidden="true">–</span>
          <DateField id="filters-m-to" label="Til dato" value={f.toDate} min={f.fromDate} onChange={(iso) => f.set({ fra: f.fromDate, til: iso < f.fromDate ? f.fromDate : iso })} testId="filters-m-to-date" />
          <div className={cn(CONTROL, 'inline-flex items-stretch overflow-hidden')}>
            <PeriodPresets f={f} />
          </div>
        </div>
      </Field>
      {f.compareMode === 'manuell' && (
        <div className="sm:col-span-2">
          <ManualRange f={f} idPrefix="filters-m" />
        </div>
      )}
      <Field label="Anlegg" htmlFor="filters-m-site">
        <SiteSelect f={f} id="filters-m-site" />
      </Field>
      <Field label="Omfang" htmlFor="filters-m-scope">
        <ScopeSelect f={f} id="filters-m-scope" />
      </Field>
    </div>
  );
}

/** Desktop: kompakt verktøylinje med alle filtre. Legges inn i toppfeltet av AppShell. */
export function FilterToolbar({ className }) {
  const f = useFilters();
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)} data-testid="filter-bar">
      <PeriodControl f={f} />
      <CompareSelect f={f} />
      {f.compareMode === 'manuell' && <ReferenceControl f={f} />}
      <ScenarioSelect f={f} />
      <MoreFilters f={f} />
      {f.chips.length > 0 && <ActiveChips f={f} />}
    </div>
  );
}

/** Mobil/nettbrett: oppsummering av valgt periode + alle filtre i skuff. */
export function FilterMobileBar() {
  const f = useFilters();
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[var(--hr-hairline)] bg-white/90 backdrop-blur-md lg:hidden" data-testid="filter-bar-mobile">
      <div className={cn(CONTAINER, 'flex items-center justify-between gap-3 py-2')}>
        <div className="min-w-0 text-sm">
          <div className="hr-tabular truncate text-[14px] font-semibold text-[var(--hr-heading)]">{formatRangeShort(f.fromDate, f.toDate)}</div>
          <div className="truncate text-[13px] text-[var(--hr-muted)]">mot {formatRangeShort(f.compare.fromDate, f.compare.toDate)} · {f.scenario.shortName}</div>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="h-9 shrink-0 rounded-[10px] bg-white text-[14px]" data-testid="filters-open-button">
              <SlidersHorizontal className="mr-1.5 h-4 w-4" aria-hidden="true" /> Filtre{f.chips.length ? ` (${f.chips.length})` : ''}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto rounded-t-[var(--hr-radius-lg)]" data-testid="filters-sheet">
            <SheetHeader className="text-left">
              <SheetTitle>Filtre</SheetTitle>
              <SheetDescription>Gjelder alle sider. Ligger i adressen, så visningen kan deles.</SheetDescription>
            </SheetHeader>
            <div className="mt-5">
              <StackedFields f={f} />
            </div>
            <div className="mt-5 flex items-center justify-between gap-2">
              <Button variant="ghost" onClick={() => f.reset()} className="h-11" data-testid="filters-reset-button-mobile">
                <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Nullstill
              </Button>
              <Button onClick={() => setOpen(false)} className="h-11 px-6" data-testid="filters-sheet-close-button">
                Ferdig
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      {f.chips.length > 0 && (
        <div className={cn(CONTAINER, 'pb-2')}>
          <ActiveChips f={f} />
        </div>
      )}
    </div>
  );
}

/** Bakoverkompatibel samlekomponent. */
export function FilterBar() {
  return (
    <>
      <div className="hidden border-b border-[var(--hr-hairline)] bg-white lg:block">
        <FilterToolbar className={cn(CONTAINER, 'py-2')} />
      </div>
      <FilterMobileBar />
    </>
  );
}
